import { ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { formatCredentialTimestamp } from "@/features/credentials/lib/format-date";
import {
  RunFindingSummary,
  RunItemProgress,
  RunStatusBadge,
  runFindingLabels,
} from "@/features/credentials/components/primary-source-runs/run-summary";
import {
  isRunActive,
  type ListedRun,
} from "@/features/credentials/components/primary-source-runs/types";
import { renderWorkspaceRunActivity } from "../../psv-run-activity";
import type { useRunHistory } from "@/features/credentials/components/primary-source-runs/use-run-history";

function RunRow({
  run,
  isLatest,
  onViewReport,
}: {
  run: ListedRun;
  isLatest: boolean;
  onViewReport: (runId: string) => void;
}) {
  const activity = renderWorkspaceRunActivity({
    run,
    summary: run.summary,
    itemSummary: run.itemSummary,
  });
  const timestamp = formatCredentialTimestamp(run.createdAt);
  return (
    <li className="flex flex-wrap items-start justify-between gap-3 border-b py-4 last:border-b-0">
      <div className="min-w-0 flex-1 space-y-1.5">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold">{timestamp}</span>
          {isLatest && (
            <span className="text-xs text-muted-foreground">
              Latest attempt
            </span>
          )}
          {activity?.badge ?? <RunStatusBadge summary={run.summary} />}
        </div>
        {activity?.showSourceFindings !== false &&
          (!activity || runFindingLabels(run.summary).length > 0) && (
            <RunFindingSummary summary={run.summary} />
          )}
        {activity?.details}
        {!activity && !isRunActive(run) && (
          <RunItemProgress summary={run.itemSummary} />
        )}
      </div>
      <Button
        variant="link"
        size="sm"
        className="gap-1"
        aria-label={`View report from ${timestamp}`}
        onClick={() => onViewReport(run.id)}
      >
        View report
        <ChevronRight className="size-4" aria-hidden />
      </Button>
    </li>
  );
}

/** Every run, newest first, each opening its own report. */
export function RunHistorySection({
  history,
  runs,
  onViewReport,
}: {
  history: ReturnType<typeof useRunHistory>;
  runs: ListedRun[];
  onViewReport: (runId: string) => void;
}) {
  return (
    <div className="space-y-3">
      {history.isLoading && (
        <Skeleton aria-hidden="true" className="h-16 rounded-xl" />
      )}
      {history.isError && (
        <div role="alert" className="space-y-2 text-sm text-destructive">
          <p>Couldn’t load verification runs.</p>
          <Button
            variant="link"
            size="sm"
            className="h-auto p-0"
            disabled={history.isFetching}
            onClick={() => void history.refetch()}
          >
            Retry runs
          </Button>
        </div>
      )}
      {runs.length > 0 && (
        <ul>
          {runs.map((run, index) => (
            <RunRow
              key={run.id}
              run={run}
              isLatest={index === 0}
              onViewReport={onViewReport}
            />
          ))}
        </ul>
      )}
      {history.hasNextPage && (
        <Button
          variant="link"
          size="sm"
          className="h-auto p-0"
          disabled={history.isFetchingNextPage}
          onClick={() => void history.fetchNextPage()}
        >
          {history.isFetchingNextPage
            ? "Loading earlier runs…"
            : "Load earlier runs"}
        </Button>
      )}
      {!history.isPending && !history.isError && runs.length === 0 && (
        <p className="text-sm text-muted-foreground">
          No primary source runs on record.
        </p>
      )}
    </div>
  );
}
